import React, { useEffect, useState } from "react";
import {
  CloudyNight,
  EyeOutline,
  LayersOutline,
  PeopleOutline,
  PlayOutline,
  Sunny,
  FlameOutline,
  ColorWandOutline,
} from "react-ionicons";
import { Video, Zap, Crown, Trophy, Sparkles, TrendingUp } from "lucide-react";
import TurboChargeCard from "../../components/TurboChargeCard";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import arthereumProvider, { db } from "../../../backend/ArthereumProvider";
import { doc, increment, updateDoc, setDoc, getDoc } from "firebase/firestore";
import { FONTSIZE, mainCryptoName } from "../../../constants";
import { PAGE_NAME, UserModelData } from "../../../types";
import AllCryptos from "../../components/AllCryptos";
import LoadingIndicator from "../../components/LoadingIndicator";
import CryptoAssetsSection from "../../components/CryptoAssetsSection";
import GrantAvailableCard from "../../components/GrantAvailableCard";
import GrantNotAvailableCard from "../../components/GrantNotAvailableCard";
import DepositPromoModal from "../../components/DepositPromoModal";

import Text from "../../components/ThemedText";
import useThemeHook from "../../hooks/useThemeHook";
import { useTranslationHook } from "../../hooks/useTranslationHook";
import { actions } from "../../redux";
import { THEME_COLORS } from "../../themes/DarkLight";
import getUserSignInData from "../../utils/getUserSignInData";
import piNetworkAdsManager from "../../utils/PiNetworkAdsManager";

import DoubleRewardsCard from "../../components/DoubleRewardsCard";
import DirectBoostCard from "../../components/DirectBoostCard";
import StreakResurrectionCard from "../../components/StreakResurrectionCard";
import FlashBoostCard from "../../components/FlashBoostCard";
import ViralPromoCard from "../../components/ViralPromoCard";
import DepositCard from "../../components/DepositCard";
import HomeGuestView from "../../components/HomeGuestView";
import DailyLoginReward from "../../components/DailyLoginReward";
import FlashBonusPopup from "../../components/FlashBonusPopup";
import HourlyBonusEvent from "../../components/HourlyBonusEvent";
import TodaysEarnings from "../../components/TodaysEarnings";

import ReferralReminder from "../../components/ReferralReminder";
import ReferralBoosterModal from "../../components/ReferralBoosterModal";
import DailyRewardModal from "../../components/DailyRewardModal";
import DailyQuests from "../../components/DailyQuests";
import VIPTierBadge from "../../components/VIPTierBadge";
import DepositStreakCard from "../../components/DepositStreakCard";

import {
  LOCAL_STORAGE_DATA,
  LocalStorageController,
} from "../../utils/LocalStorageController";
import AppLayout from "../../../components/AppLayout";

/**
 * HomePage — Wrapper component that handles authentication detection.
 * Shows HomeGuestView for unauthenticated users, HomePageContent for authenticated.
 */
const HomePage = () => {
  const userData: UserModelData = useSelector(
    (state) => (state as any)?.actualUser
  );
  const dispatch = useDispatch();
  const [authChecked, setAuthChecked] = useState(!!userData?.user?.id);
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      // Already authenticated via redux
      if (userData?.user?.id) {
        setIsGuest(false);
        setAuthChecked(true);
        return;
      }

      const initialized = LocalStorageController.connect().getData(
        LOCAL_STORAGE_DATA.SYSTEM_FULLY_INITIALIZED,
        "string"
      );

      if (initialized === "true") {
        // Returning user — try silent auto-login
        try {
          const creds = await getUserSignInData();
          const user = await arthereumProvider.signIn(creds);
          dispatch(actions.setActualUser(user));
          setIsGuest(false);
        } catch {
          setIsGuest(true);
        }
      } else {
        setIsGuest(true);
      }
      setAuthChecked(true);
    };
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authChecked) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <LoadingIndicator />
      </div>
    );
  }

  if (isGuest) {
    return <HomeGuestView />;
  }

  return (
    <AppLayout>
      <HomePageContent />
    </AppLayout>
  );
};

const ListingAnnouncementCard = () => {
  const theme = useThemeHook();
  const navigate = useNavigate();
  const userData = useSelector((state: any) => state?.actualUser);
  const userId = userData?.user?.id;

  return (
    <div className="relative overflow-hidden rounded-3xl p-5 border-2 border-indigo-500/30 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white shadow-2xl shadow-indigo-500/20">
      {/* Glow Effects */}
      <div className="absolute -right-10 -top-10 w-28 h-28 bg-purple-500/25 rounded-full blur-2xl animate-pulse" />
      <div className="absolute -left-10 -bottom-10 w-28 h-28 bg-blue-500/25 rounded-full blur-2xl animate-pulse" />

      {/* Confirmed Listing Badge */}
      <div className="relative z-10 flex items-center justify-between mb-4">
        <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-indigo-500/20 border border-indigo-500/40 text-[9px] font-black uppercase tracking-widest text-indigo-300 animate-pulse">
          <Sparkles size={9} className="text-indigo-400" /> Listing Confirmed
        </span>
        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
          Preparation Phase
        </span>
      </div>

      {/* Exchange Names Header */}
      <div className="relative z-10 mb-2">
        <h3 className="text-base font-black tracking-tight leading-snug bg-gradient-to-r from-white via-indigo-100 to-indigo-300 bg-clip-text text-transparent">
          ARTHEREUM (ATH) LISTING SOON!
        </h3>
        <div className="flex flex-wrap gap-2 items-center mt-1">
          <span className="px-2 py-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 rounded text-[9px] font-black text-white uppercase tracking-wider shadow-sm">
            Gate.io
          </span>
          <span className="text-[10px] font-semibold text-indigo-400">and</span>
          <span className="px-2 py-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded text-[9px] font-black text-white uppercase tracking-wider shadow-sm">
            Bitunix
          </span>
        </div>
      </div>

      {/* Motivational / Faith Message */}
      <p className="relative z-10 text-[11px] text-slate-300 leading-relaxed mb-4">
        The future of Arthereum is bright! We are listing on top crypto exchanges. 
        Your loyalty and dedication have made this possible. Have faith in the vision—together we are building an ecosystem with real long-term value.
      </p>

      {/* Rewarded Ad Booster Box */}
      <div className="relative z-10 p-3 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md">
        <div className="flex items-start gap-2 mb-3">
          <Zap size={15} className="text-amber-400 fill-amber-400 mt-0.5 shrink-0 animate-bounce" />
          <p className="text-[10px] font-medium text-slate-200 leading-normal">
            <strong>Boost liquidity & listings:</strong> Every ad you watch increases Arthereum's value and awards you massive mining bonuses. Let's pump our stash before trading starts!
          </p>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => {
              piNetworkAdsManager.handleRewardedAdFlowFront(userId);
            }}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 px-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 active:scale-95 text-[10px] font-black uppercase text-white rounded-xl transition-all shadow-md shadow-orange-500/25"
          >
            <Video size={11} fill="currentColor" /> Watch Ad
          </button>
          <button
            onClick={() => navigate(PAGE_NAME.WATCH_AND_EARN)}
            className="flex-1 flex items-center justify-center py-1.5 px-2.5 bg-white/10 hover:bg-white/20 active:scale-95 text-[10px] font-black uppercase text-white rounded-xl transition-all"
          >
            Ad Hub 📺
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * HomePageContent — The full authenticated home page experience.
 * Only rendered when user is confirmed authenticated.
 */
const HomePageContent = () => {
  const userData: UserModelData = useSelector(
    (state) => (state as any)?.actualUser
  );

  const getTranslationText = useTranslationHook();
  const theme = useThemeHook();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const onOptionPress = (path: string) => navigate(path);
  const watchAds = () => {
    piNetworkAdsManager.handleRewardedAdFlowFront(userData?.user?.id);
  };

  const trackDepositClick = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const analyticsRef = doc(db, "athAnalytics", `deposit_clicks_${today}`);
      const docSnap = await getDoc(analyticsRef);
      
      if (docSnap.exists()) {
        await updateDoc(analyticsRef, {
          deposit_button_clicks: increment(1),
          lastUpdated: new Date()
        });
      } else {
        await setDoc(analyticsRef, {
          date: today,
          deposit_button_clicks: 1,
          lastUpdated: new Date()
        });
      }
    } catch (error) {
      console.log('Analytics tracking error:', error);
    }
  };

  const options = [
    {
      onPress: () => onOptionPress(PAGE_NAME.INVITE_USERS),
      id: 1,
      iconName: (
        <PeopleOutline
          height={FONTSIZE.SECONDARY}
          width={FONTSIZE.SECONDARY}
          color={THEME_COLORS[theme].gray}
        />
      ),
      title: getTranslationText("invite"),
    },
    {
      onPress: watchAds,
      id: 2,
      iconName: (
        <PlayOutline
          height={FONTSIZE.SECONDARY}
          width={FONTSIZE.SECONDARY}
          color={THEME_COLORS[theme].gray}
        />
      ),
      title: getTranslationText("watch"),
    },
    {
      onPress: () => alert(getTranslationText("prove_humanness_desc")),
      id: 4,
      iconName: (
        <EyeOutline
          height={FONTSIZE.SECONDARY}
          width={FONTSIZE.SECONDARY}
          color={THEME_COLORS[theme].gray}
        />
      ),
      title: getTranslationText("humanness"),
    },
    {
      onPress: () => onOptionPress(PAGE_NAME.HYSTORY_PAGE),
      id: 5,
      iconName: (
        <LayersOutline
          height={FONTSIZE.SECONDARY}
          width={FONTSIZE.SECONDARY}
          color={THEME_COLORS[theme].gray}
        />
      ),
      title: getTranslationText("history"),
    },
    {
      onPress: () => onOptionPress(PAGE_NAME.LUCKY_SPIN),
      id: 6,
      iconName: (
        <ColorWandOutline
          height={FONTSIZE.SECONDARY}
          width={FONTSIZE.SECONDARY}
          color="#8b5cf6"
        />
      ),
      title: "Lucky Spin",
    },
    {
      onPress: () => onOptionPress(PAGE_NAME.PRO),
      id: 7,
      iconName: (
        <Crown
          size={FONTSIZE.SECONDARY}
          color="#f59e0b"
        />
      ),
      title: "Go Pro",
    },
    {
      onPress: () => onOptionPress(PAGE_NAME.LEADERBOARD),
      id: 8,
      iconName: (
        <Trophy
          size={FONTSIZE.SECONDARY}
          color="#6366f1"
        />
      ),
      title: "Ranking",
    },
  ];

  const referrals = useSelector((state: any) => state?.userReferrals);
  const [isLoadingReferrals, setIsLoadingReferrals] = useState(
    referrals?.length !== 0 ? false : true
  );

  const getReferrals = async () => {
    if (referrals?.length !== 0) {
      return;
    }
    try {
      const rrs = await arthereumProvider.getUserReferrals(
        userData?.user?.id,
        null
      );
      dispatch(actions.setUserReferrals(rrs.referrals));
    } catch (error) {
    } finally {
      setIsLoadingReferrals(false);
    }
  };

  useEffect(() => {
    getReferrals();
  }, [userData?.user?.id]);

  const [grantAvailable, setGrantAvailable] = useState(false);
  const [nextMiningDate, setNextMiningDate] = useState(undefined);

  useEffect(() => {
    const lastMining = new Date(userData.user?.lastMiningDate?.seconds * 1000);
    const actualDate = new Date();
    const diffHours =
      Math.abs(actualDate?.getTime() - lastMining.getTime()) / 36e5;

    if (diffHours < 2) {
      setGrantAvailable(false);
      const _nextMiningDate = new Date(
        lastMining.getTime() + 2 * 60 * 60 * 1000
      );
      setNextMiningDate(_nextMiningDate as any);
    } else {
      setGrantAvailable(true);
    }
  }, [userData]);

  const onSeeMore = () => {
    navigate(PAGE_NAME.REFERRALS);
  };

  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    if (refreshing) {
      return;
    }
    try {
      setRefreshing(true);
      const user = await arthereumProvider.getUserModelDataByUid(userData?.user?.id);
      dispatch(actions.setActualUser(user));
    } catch (error) {
    } finally {
      setRefreshing(false);
    }
  };

  // Smart-paced rewarded ad on HomePage — uses session-depth intervals
  useEffect(() => {
    // Only run in production
    if (process.env.NODE_ENV === 'development') return;
    
    // Reset session tracking on mount
    piNetworkAdsManager.resetSession();
    
    let adTimerRef: NodeJS.Timeout | null = null;
    
    const scheduleNextAd = () => {
      const interval = piNetworkAdsManager.getOptimalInterval();
      if (interval === Infinity) {
        // Too early in session, check again in 30 seconds
        adTimerRef = setTimeout(scheduleNextAd, 30 * 1000);
        return;
      }
      
      const lastAdTime = localStorage.getItem("lastHomePageAdTime");
      const currentTime = Date.now();
      
      if (!lastAdTime || currentTime - Number(lastAdTime) >= interval) {
        piNetworkAdsManager.handleRewardedAdFlowFront(userData?.user?.id);
        localStorage.setItem("lastHomePageAdTime", currentTime.toString());
      }
      
      adTimerRef = setTimeout(scheduleNextAd, interval);
    };
    
    // Start the scheduling loop
    scheduleNextAd();
    
    return () => {
      if (adTimerRef) clearTimeout(adTimerRef);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency - only run once on mount

  // State for referral pop-ups and daily reward
  const [showReferralReminder, setShowReferralReminder] = useState(false);
  const [showReferralBooster, setShowReferralBooster] = useState(false);
  const [boosterEndTime, setBoosterEndTime] = useState<Date | null>(null);
  const [showDailyReward, setShowDailyReward] = useState(false);
  const [showDepositPromo, setShowDepositPromo] = useState(false);

  // Auto-trigger Deposit Promotion Modal on mount (once per 24 hours after 3s delay)
  useEffect(() => {
    const lastPromoSeen = localStorage.getItem("lastDepositPromoSeen");
    const now = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;

    if (!lastPromoSeen || now - Number(lastPromoSeen) > twentyFourHours) {
      const timer = setTimeout(() => {
        setShowDepositPromo(true);
        localStorage.setItem("lastDepositPromoSeen", now.toString());
      }, 3000); // 3 seconds delay
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <DepositPromoModal isOpen={showDepositPromo} onClose={() => setShowDepositPromo(false)} />

      
      {/* Referral Pop-ups */}
      {showReferralReminder && (
        <ReferralReminder 
          referralCount={userData?.user?.referalsCount || 0}
          onDismiss={() => setShowReferralReminder(false)}
        />
      )}
      <ReferralBoosterModal 
        isOpen={showReferralBooster}
        onClose={() => setShowReferralBooster(false)}
        endTime={boosterEndTime || undefined}
      />
      
      {/* Container */}
      <div className="max-w-md mx-auto">
        {/* Crypto Ticker - Compact */}
        <div className="border-b border-gray-100">
          <AllCryptos row />
        </div>





        {/* Main Content */}
        <div className="px-3 py-3 pb-24 space-y-3">
          {/* Exchange Listing Announcement */}
          <ListingAnnouncementCard />

          {/* Grant Card - Priority Position (Moved to the absolute Top) */}
          <div className="pt-1">
            {grantAvailable ? (
              <GrantAvailableCard
                nextGrantValue={userData?.miningInfos?.nextGrantValue}
              />
            ) : (
              <GrantNotAvailableCard targetDate={nextMiningDate} />
            )}
          </div>

          {/* Prominent Deposit Section — Top position for visibility */}
          <DepositCard 
            onClick={() => {
              trackDepositClick();
              setShowDepositPromo(true);
            }} 
          />

          {/* VIP Tier Progress */}
          <VIPTierBadge />

          {/* Deposit Streak */}
          <DepositStreakCard />

          {/* Daily Streak Calendar — NEW */}
          <DailyLoginReward />

          {/* Hourly Bonus Event — NEW */}
          <HourlyBonusEvent />

          {/* Today's Earnings Dashboard — NEW */}
          <TodaysEarnings />




          {/* Balance Section - Minimal */}
          <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
            <div className="space-y-4 mb-4">
              {/* Row 1: Status Badges - Scalable & Wrapping */}
              <div className="flex flex-wrap items-center gap-2">
                {userData.user?.currentStreak > 0 && (
                  <div className="flex items-center gap-1.5 bg-orange-50 px-2.5 py-1 rounded-full border border-orange-100">
                    <FlameOutline color="#f97316" height="14px" width="14px" />
                    <span className="text-[10px] font-black text-orange-600 uppercase italic">
                      {userData.user.currentStreak} Day Streak
                    </span>
                  </div>
                )}
                {userData.user?.luckyTagExpiry && new Date(userData.user.luckyTagExpiry.seconds * 1000) > new Date() && (
                  <div className="flex items-center gap-1.5 bg-purple-50 px-2.5 py-1 rounded-full border border-purple-100">
                    <ColorWandOutline color="#8b5cf6" height="14px" width="14px" />
                    <span className="text-[10px] font-black text-purple-600 italic">LUCKY ✨</span>
                  </div>
                )}
                {userData.user?.turboChargeExpiry && new Date(userData.user.turboChargeExpiry.seconds * 1000) > new Date() && (
                  <div className="flex items-center gap-1.5 bg-yellow-50 px-2.5 py-1 rounded-full border border-yellow-100">
                    <Zap size={14} className="text-yellow-600" fill="currentColor" />
                    <span className="text-[10px] font-black text-yellow-600 italic">TURBO ACTIVE ⚡</span>
                  </div>
                )}
              </div>

              {/* Row 2: Grant Context - Clean & Informative */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center">
                    {grantAvailable ? (
                      <CloudyNight
                        color={THEME_COLORS[theme].gray}
                        height="18px"
                        width="18px"
                      />
                    ) : (
                      <Sunny
                        color={THEME_COLORS[theme].gold}
                        height="18px"
                        width="18px"
                      />
                    )}
                  </div>
                  <span className="text-[10px] text-gray-500 font-bold uppercase tracking-tight">
                    {getTranslationText("total_grants_claimed")}
                  </span>
                </div>
                <span className="text-[10px] text-gray-400 font-semibold italic">
                  {getTranslationText("grants_available_each_24h")}
                </span>
              </div>
            </div>
            
            <div className="text-center py-2">
              <div className="text-4xl font-bold text-gray-900">
                {userData?.user?.arthereumBalance?.toFixed(2)}
              </div>
              <div className="text-sm text-gray-400 mt-1">Arthereum</div>
            </div>
          </div>


          {/* Action Buttons - Compact Grid */}
          <div className="grid grid-cols-4 gap-2 px-1">
            {options?.map((op) => (
              <button
                key={op.id}
                onClick={op.onPress}
                className={`flex flex-col items-center gap-1.5 p-2 rounded-xl transition-colors ${
                  theme === "dark"
                    ? "hover:bg-gray-800 active:bg-gray-700"
                    : "hover:bg-gray-50 active:bg-gray-100"
                }`}
              >
                <div className={`w-11 h-11 rounded-full flex items-center justify-center ${
                  theme === "dark" ? "bg-gray-800" : "bg-gray-50 shadow-sm border border-gray-100"
                }`}>
                  {op.iconName}
                </div>
                <span className={`text-[10px] font-medium text-center leading-none ${
                  theme === "dark" ? "text-gray-400" : "text-gray-600"
                }`}>
                  {op.title}
                </span>
              </button>
            ))}
          </div>

          {/* Streak Resurrection - Loss Aversion Hook */}
          <StreakResurrectionCard />

          {/* Flash Boost - FOMO Event */}
          <FlashBoostCard />

          {/* Viral Promo - New Growth Engine */}
          <ViralPromoCard />



          {/* Double Rewards / Boost Cards */}
          <div className="space-y-3">
             <DailyQuests />
             <button 
               onClick={() => navigate(PAGE_NAME.WATCH_AND_EARN)}
               className={`w-full text-left p-4 rounded-2xl border transition-all active:scale-95 ${
                 theme === "dark" 
                   ? "bg-slate-800 border-slate-700 hover:bg-slate-700" 
                   : "bg-white border-gray-100 hover:bg-gray-50 shadow-sm"
               }`}
             >
               <div className="flex items-center gap-4">
                 <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                   theme === "dark" ? "bg-amber-900/30" : "bg-amber-50"
                 }`}>
                   <Video size={24} className="text-amber-500" fill="currentColor" />
                 </div>
                 <div className="flex-1 min-w-0">
                   <div className="flex items-center gap-2 mb-1">
                     <h3 className={`font-bold truncate ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                       Watch & Earn 📺
                     </h3>
                     <span className="px-2 py-0.5 rounded-md bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-black uppercase tracking-wider animate-pulse">
                       30 ADS
                     </span>
                   </div>
                   <p className={`text-xs truncate ${theme === "dark" ? "text-slate-400" : "text-gray-500"}`}>
                     Earn up to 47.5 ATH daily — 6 reward tiers! 💎
                   </p>
                 </div>
               </div>
             </button>
              <TurboChargeCard 
                count={userData.user?.turboChargeCount || 0} 
                expiry={userData.user?.turboChargeExpiry} 
                onClick={watchAds} 
              />
              <DirectBoostCard />
           </div>

          {/* Referrals Section */}
          <div className="pt-2">
            {isLoadingReferrals ? (
              <div className="flex items-center justify-center py-8">
                <LoadingIndicator />
              </div>
            ) : (
              <CryptoAssetsSection onSeeMore={onSeeMore} referrals={referrals} />
            )}
          </div>
        </div>
      </div> 
      
      {/* Flash Bonus Popup — floats over the page */}
      <FlashBonusPopup />
      
      {showDailyReward && (
        <DailyRewardModal onClose={() => setShowDailyReward(false)} />
      )}
    </div>
  );
};

export default HomePage;